/**
 * coverBrandAffordances — K5 print-safe, token-styled SVG brand affordances
 * for the cover + dark feature pages:
 *
 *   - CropMarks    — corner registration marks at the four page corners.
 *   - CoverDatumStrip — a dotted engineering "datum strip" ruler (the mono
 *     coordinate strips), optionally labelled left/centre/right.
 *   - ConcentricRings — the faint reactor-datum rings the dark cover lays the
 *     title over.
 *
 * Every affordance is pure SVG (no raster, no web fonts), so it survives the
 * Paged.js / Puppeteer print path identically to the on-screen view. Colours
 * are passed in by the caller from a resolved role/dark-surface token — these
 * primitives carry NO literal hex of their own.
 *
 * GATING: CropMarks + CoverDatumStrip are OFF by default. The cover only mounts
 * them when `coverBrandAffordancesEnabled(theme, …)` is true (a theme/template
 * opt-in). ConcentricRings is intrinsic to the dark-instrument cover (its
 * defining motif), not a separately gated affordance.
 */
import React from 'react';
import {
  COVER_DATUM_DASH_ARRAY,
  COVER_DATUM_STROKE_WIDTH,
  COVER_CROP_MARK_LENGTH_PX,
  COVER_CROP_MARK_STROKE_WIDTH,
} from '../../../../lib/reportCoverTokens';

/**
 * Corner crop / registration marks. An absolutely-positioned overlay that
 * paints an L at each of the four corners. `color` is a resolved token
 * (typically the on-dark hairline or the light-page rule). `inset` pulls the
 * marks in from the edge so they sit inside the printable frame.
 */
export function CropMarks({
  color,
  length = COVER_CROP_MARK_LENGTH_PX,
  strokeWidth = COVER_CROP_MARK_STROKE_WIDTH,
  testId,
}: {
  color: string;
  length?: number;
  strokeWidth?: number;
  testId?: string;
}): React.ReactElement {
  const corner = (
    transform: string,
    key: string,
  ): React.ReactElement => (
    // Each corner is two strokes meeting at the corner point (an L).
    <g transform={transform} key={key} stroke={color} strokeWidth={strokeWidth}>
      <line x1={0} y1={0} x2={length} y2={0} />
      <line x1={0} y1={0} x2={0} y2={length} />
    </g>
  );
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      preserveAspectRatio="none"
      aria-hidden="true"
      data-testid={testId ?? 'cover-crop-marks'}
      data-cover-affordance="crop-marks"
      // The viewBox is a normalized 100×100; the corners scale to the box via
      // preserveAspectRatio="none". Stroke widths are in user units (≈px at the
      // cover's rendered size) so the hairline stays crisp.
      viewBox="0 0 100 100"
    >
      {corner('translate(0,0)', 'tl')}
      {corner('translate(100,0) scale(-1,1)', 'tr')}
      {corner('translate(0,100) scale(1,-1)', 'bl')}
      {corner('translate(100,100) scale(-1,-1)', 'br')}
    </svg>
  );
}

/**
 * A dotted engineering datum strip — a full-width dashed rule, with optional
 * mono labels above/below at the left / centre / right (the redesign's
 * coordinate strip). `color` styles the rule; `labelColor` the labels.
 */
export function CoverDatumStrip({
  color,
  labelColor,
  left,
  center,
  right,
  fontFamily,
  testId,
}: {
  color: string;
  labelColor?: string;
  left?: string;
  center?: string;
  right?: string;
  fontFamily?: string;
  testId?: string;
}): React.ReactElement {
  const hasLabels = !!(left || center || right);
  return (
    <div
      className="w-full"
      data-testid={testId ?? 'cover-datum-strip'}
      data-cover-affordance="datum-strip"
    >
      <svg
        className="block w-full"
        height={COVER_DATUM_STROKE_WIDTH}
        preserveAspectRatio="none"
        aria-hidden="true"
        viewBox={`0 0 1000 ${COVER_DATUM_STROKE_WIDTH}`}
      >
        <line
          x1={0}
          y1={COVER_DATUM_STROKE_WIDTH / 2}
          x2={1000}
          y2={COVER_DATUM_STROKE_WIDTH / 2}
          stroke={color}
          strokeWidth={COVER_DATUM_STROKE_WIDTH}
          strokeDasharray={COVER_DATUM_DASH_ARRAY}
        />
      </svg>
      {hasLabels && (
        <div
          className="mt-1 flex items-baseline justify-between text-[8px] uppercase tracking-[0.22em] tabular-nums"
          style={{ color: labelColor ?? color, fontFamily }}
        >
          <span data-testid="cover-datum-left">{left ?? ''}</span>
          <span data-testid="cover-datum-center">{center ?? ''}</span>
          <span className="text-right" data-testid="cover-datum-right">{right ?? ''}</span>
        </div>
      )}
    </div>
  );
}

/**
 * The faint concentric reactor-datum rings the dark cover lays the title over.
 * Centred on a point (default upper-right, mirroring the reference); `color` is
 * a resolved low-alpha on-dark token so it whispers behind the title.
 */
export function ConcentricRings({
  color,
  cx = 62,
  cy = 32,
  testId,
}: {
  color: string;
  cx?: number;
  cy?: number;
  testId?: string;
}): React.ReactElement {
  // Eight rings at increasing radii — purely decorative datum texture.
  const radii = [8, 14, 20, 26, 32, 38, 44, 50];
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      data-testid={testId ?? 'cover-concentric-rings'}
      data-cover-affordance="concentric-rings"
      viewBox="0 0 100 100"
    >
      {radii.map((r) => (
        <circle
          key={r}
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={0.15}
        />
      ))}
    </svg>
  );
}
